<template>
  <div class="todo-category-container">
    <a-form layout="inline" class="search-form">
      <a-form-item>
        <a-input v-model="searchForm.category_path" placeholder="输入分类路径" @pressEnter="handleSearch" allow-clear />
      </a-form-item>
      <a-form-item>
        <a-button type="primary" @click="handleSearch">搜索</a-button>
        <a-button @click="handleReset">重置</a-button>
      </a-form-item>
    </a-form>

    <div class="action-bar">
      <a-button v-permission="PERMISSIONS.TODO_CATEGORY_MANAGE" @click="showCreateModal"> <a-icon type="plus" /> 新建分类 </a-button>
    </div>

    <div class="table-wrapper">
      <a-table
        :columns="columns"
        :data-source="dataSource"
        :pagination="false"
        :loading="loading"
        rowKey="id"
        size="middle"
        bordered
        :scroll="{ y: tableScrollY }"
      >
        <template slot="level" slot-scope="level">
          <a-tag :color="levelColorMap[level]">
            {{ levelMap[level] || level }}
          </a-tag>
        </template>

        <template slot="operation" slot-scope="record">
          <div class="action-buttons">
            <a-button size="small" v-permission="PERMISSIONS.TODO_CATEGORY_MANAGE" @click="showEditModal(record)"> 编辑 </a-button>
            <a-popconfirm title="确定删除该分类？" @confirm="() => handleDelete(record.id)">
              <a-button size="small" v-permission="PERMISSIONS.TODO_CATEGORY_MANAGE">删除</a-button>
            </a-popconfirm>
          </div>
        </template>
      </a-table>
    </div>

    <div class="pagination-wrapper">
      <a-pagination
        v-model="pagination.current"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        :page-size-options="['20', '50', '100', '200']"
        show-size-changer
        show-quick-jumper
        :show-total="total => `共 ${total} 条`"
        @change="handlePageChange"
        @showSizeChange="handlePageSizeChange"
      />
    </div>

    <a-modal :title="isEditMode ? '编辑分类' : '新建分类'" v-model="modalVisible" @ok="handleSubmit" :confirm-loading="submitting">
      <a-form :form="form" layout="vertical">
        <a-form-item label="分类路径" required>
          <a-input v-decorator="['category_path', { rules: [{ required: true, message: '必填项' }] }]" />
        </a-form-item>

        <a-form-item label="等级" required>
          <a-select v-decorator="['level', { rules: [{ required: true, message: '请选择等级' }] }]">
            <a-select-option v-for="level in levelOptions" :key="level.value" :value="level.value">
              {{ level.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="分类描述">
          <a-textarea v-decorator="['category_desc']" :rows="3" />
        </a-form-item>

        <div class="config-editor">
          <a-form-item label="编号前缀" required>
            <a-input
              v-decorator="[
                'code',
                {
                  rules: [
                    { required: true, message: '必填项' },
                    { pattern: /^[A-Z_]+$/, message: '只能包含大写字母和下划线' }
                  ]
                }
              ]"
              style="width: 200px"
            />
            <a-tooltip title="待办编号">
              <a-icon type="question-circle" style="margin-left: 8px" />
            </a-tooltip>
          </a-form-item>

          <a-form-item label="冷却期" required>
            <a-input-number
              v-decorator="[
                'cooldown_days',
                {
                  rules: [
                    { required: true, message: '必填项' },
                    { type: 'number', min: 0, max: 99 }
                  ]
                }
              ]"
              :min="0"
              :max="99"
              style="width: 120px"
            />
            <span class="unit">天</span>
            <a-tooltip title="待办取消后，多少天内可以重新创建，0为永不创建">
              <a-icon type="question-circle" style="margin-left: 8px" />
            </a-tooltip>
          </a-form-item>

          <a-form-item label="处理时长" required>
            <a-input
              v-decorator="[
                'handle_duration',
                {
                  rules: [{ required: true, message: '必填项' }]
                }
              ]"
              style="width: 200px"
              placeholder="示例：1d 或 12h"
            />
            <a-tooltip title="支持单位：h(小时), d(天)">
              <a-icon type="question-circle" style="margin-left: 8px" />
            </a-tooltip>
          </a-form-item>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { getCategoryList, deleteCategory, createCategory, updateCategory, getCategory } from '@/api/todo'

const LEVEL_OPTIONS = [
  { value: 'DEBUG', label: '调试', color: 'blue' },
  { value: 'INFO', label: '信息', color: 'green' },
  { value: 'WARN', label: '警告', color: 'orange' },
  { value: 'ERROR', label: '错误', color: 'red' },
  { value: 'CRITICAL', label: '严重', color: 'volcano' },
  { value: 'FATAL', label: '致命', color: 'magenta' }
]

export default {
  data() {
    return {
      form: this.$form.createForm(this),
      searchForm: {
        category_path: ''
      },
      pagination: {
        current: 1,
        pageSize: 20,
        total: 0
      },
      dataSource: [],
      loading: false,
      modalVisible: false,
      submitting: false,
      isEditMode: false,
      currentId: null,
      tableScrollY: 0,
      levelOptions: LEVEL_OPTIONS,
      levelMap: LEVEL_OPTIONS.reduce((acc, cur) => {
        acc[cur.value] = cur.label
        return acc
      }, {}),
      levelColorMap: LEVEL_OPTIONS.reduce((acc, cur) => {
        acc[cur.value] = cur.color
        return acc
      }, {}),
      columns: [
        {
          title: '分类路径',
          dataIndex: 'category_path',
          width: 500
        },
        {
          title: '等级',
          dataIndex: 'level',
          scopedSlots: { customRender: 'level' },
          width: 120
        },
        {
          title: '描述',
          dataIndex: 'category_desc',
          ellipsis: true
        },
        {
          title: '创建时间',
          dataIndex: 'create_time',
          width: 180
        },
        {
          title: '创建人',
          dataIndex: 'create_user',
          width: 180
        },
        {
          title: '操作',
          scopedSlots: { customRender: 'operation' },
          width: 150
        }
      ]
    }
  },

  mounted() {
    this.loadData()
    this.calcTableHeight()
    window.addEventListener('resize', this.calcTableHeight)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.calcTableHeight)
  },

  methods: {
    calcTableHeight() {
      const pageHeight = window.innerHeight
      const headerHeight = 64
      const contentMargin = 48
      const contentPadding = 48
      const paginationHeight = 52
      const container = document.querySelector('.todo-category-container .search-form')
      const actionBar = document.querySelector('.todo-category-container .action-bar')
      const searchHeight = container ? container.clientHeight : 52
      const actionHeight = actionBar ? actionBar.clientHeight + 16 : 0
      const tableHeaderHeight = 55
      const extraGap = 10
      this.tableScrollY =
        pageHeight - headerHeight - contentMargin - contentPadding - searchHeight - actionHeight - paginationHeight - tableHeaderHeight - extraGap
    },

    async loadData() {
      try {
        this.loading = true
        const params = {
          page_num: this.pagination.current,
          page_size: this.pagination.pageSize,
          ...this.searchForm
        }

        const { data } = await getCategoryList(params)
        this.dataSource = data.list
        this.pagination.total = data.total
      } catch (error) {
        this.$message.error('数据加载失败')
      } finally {
        this.loading = false
      }
    },

    handleSearch() {
      this.pagination.current = 1
      this.loadData()
    },

    handleReset() {
      this.searchForm = { category_path: '' }
      this.handleSearch()
    },

    handlePageChange(page) {
      this.pagination.current = page
      this.loadData()
    },

    handlePageSizeChange(current, size) {
      this.pagination.pageSize = size
      this.handleSearch()
    },

    showCreateModal() {
      this.isEditMode = false
      this.modalVisible = true

      this.form.resetFields()
      this.form.setFieldsValue({
        level: 'DEBUG',
        cooldown_days: 7,
        handle_duration: '7d'
      })
    },

    async showEditModal(record) {
      try {
        this.loading = true
        const { data } = await getCategory(record.id)
        this.modalVisible = true
        this.isEditMode = true
        this.currentId = record.id

        await this.$nextTick()

        this.form.setFieldsValue({
          category_path: data.category_path,
          level: data.level,
          category_desc: data.category_desc,
          code: data.category_config.code,
          cooldown_days: data.category_config.cooldown_days,
          handle_duration: data.category_config.handle_duration
        })
      } catch (error) {
        this.$message.error('获取详情失败')
      } finally {
        this.loading = false
      }
    },

    handleSubmit() {
      this.form.validateFields(async (err, values) => {
        if (err) return

        try {
          this.submitting = true
          const payload = {
            level: values.level,
            category_path: values.category_path,
            category_desc: values.category_desc,
            category_config: {
              code: values.code,
              cooldown_days: values.cooldown_days,
              handle_duration: values.handle_duration
            }
          }

          if (this.isEditMode) {
            await updateCategory(this.currentId, payload)
          } else {
            await createCategory(payload)
          }

          this.$message.success('操作成功')
          this.modalVisible = false
          this.loadData()
        } catch (error) {
          this.$message.error(`操作失败: ${error.message}`)
        } finally {
          this.submitting = false
        }
      })
    },

    async handleDelete(id) {
      try {
        await deleteCategory(id)
        this.$message.success('删除成功')

        if (this.dataSource.length === 1 && this.pagination.current > 1) {
          this.pagination.current--
        }

        this.loadData()
      } catch (error) {
        this.$message.error(`删除失败: ${error.message}`)
      }
    }
  }
}
</script>

<style scoped>
.todo-category-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.search-form {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.action-bar {
  margin-bottom: 10px;
  flex-shrink: 0;
}

.table-wrapper {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 10px;
  flex-shrink: 0;
}

:deep .ant-form-item-control {
  line-height: 0;
}

.config-editor {
  padding-left: 20px;
  border-left: 2px solid #ebeef5;
  margin-left: 10px;
}

.config-editor .ant-form-item {
  margin-bottom: 20px;
}

.unit {
  margin-left: 10px;
}

.action-buttons {
  display: flex;
  gap: 8px;
}
</style>
